import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { useAuth } from '../../contexts/AuthContext';
import { clearPendingCheckout } from '../../lib/pendingCheckout';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const POLL_INTERVAL_MS = 2000;
// Extended from 10 → 20 polls (20s → 40s). Subscription activation is the
// most-impactful Stripe event we render — preferring an extra few seconds of
// wait over a false-failure page is a high-value tradeoff here.
const MAX_POLL_ATTEMPTS = 20;
const SUCCESS_REDIRECT_MS = 3000;
const TIMEOUT_MESSAGE =
  'Still processing on Stripe\u2019s side. Your subscription will activate shortly — refresh Settings in a minute, or contact support if you don\u2019t see it.';

export default function SubscribeReturn() {
  const navigate = useNavigate();
  const { authToken, user } = useAuth();
  const [state, setState] = useState({ phase: 'polling', data: null, error: null });
  const [attempts, setAttempts] = useState(0);

  // Tenant-scoped destinations. Super Admin (no workspaceId) is unlikely to
  // ever hit this page (subscriptions are per-tenant) but we route them to
  // the admin dashboard as a safe fallback.
  const settingsPath = user?.workspaceId
    ? `/${user.workspaceId}/settings`
    : '/admin/dashboard';
  const subscribePath = user?.workspaceId
    ? `/${user.workspaceId}/billing/subscribe`
    : '/admin/dashboard';
  const dashboardPath = user?.workspaceId
    ? `/${user.workspaceId}/dashboard`
    : '/admin/dashboard';

  const pollStatus = useCallback(async (sessionId) => {
    // v3.10.7: two-tier probe. Authenticated endpoint first; if the
    // user lost their JWT (incognito flow), fall back to the public
    // status endpoint so the UI tells the truth instead of a misleading
    // failure card. Side-effect (subscription activation) is applied
    // by both paths idempotently — and by the Stripe webhook regardless.
    try {
      if (authToken) {
        const res = await fetch(
          `${API_URL}/api/billing/checkout/status/${sessionId}`,
          { headers: { Authorization: `Bearer ${authToken}` } },
        );
        if (res.ok) {
          const data = await res.json();
          if (data.status === 'COMPLETED') {
            setState({ phase: 'success', data, error: null, anonymous: false });
            return;
          }
          if (data.status === 'FAILED' || data.status === 'EXPIRED') {
            setState({ phase: 'failed', data, error: null, anonymous: false });
            return;
          }
          setAttempts((a) => a + 1);
          return;
        }
        if (res.status !== 401) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || `HTTP ${res.status}`);
        }
      }

      const pubRes = await fetch(
        `${API_URL}/api/billing/checkout/status-public/${sessionId}`,
      );
      if (!pubRes.ok) {
        const err = await pubRes.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${pubRes.status}`);
      }
      const pubData = await pubRes.json();
      if (pubData.status === 'COMPLETED') {
        setState({ phase: 'success', data: pubData, error: null, anonymous: true });
      } else if (pubData.status === 'FAILED' || pubData.status === 'EXPIRED') {
        setState({ phase: 'failed', data: pubData, error: null, anonymous: true });
      } else {
        setAttempts((a) => a + 1);
      }
    } catch (e) {
      setState({ phase: 'failed', data: null, error: e.message, anonymous: !authToken });
    }
  }, [authToken]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) {
      setState({ phase: 'failed', data: null, error: 'Missing session_id in URL' });
      return;
    }
    pollStatus(sessionId);
  }, [pollStatus]);

  useEffect(() => {
    if (state.phase !== 'polling') return;
    if (attempts >= MAX_POLL_ATTEMPTS) {
      setState({
        phase: 'failed', data: null,
        error: TIMEOUT_MESSAGE,
      });
      return;
    }
    const sessionId = new URLSearchParams(window.location.search).get('session_id');
    if (!sessionId) return;
    const t = setTimeout(() => pollStatus(sessionId), POLL_INTERVAL_MS);
    return () => clearTimeout(t);
  }, [attempts, state.phase, pollStatus]);

  // Auto-redirect on success — drops the user at their dashboard so the
  // newly active plan badge / seat count is immediately visible.
  // Anonymous (incognito) returns require explicit Sign In; skip the bounce.
  useEffect(() => {
    if (state.phase !== 'success' && state.phase !== 'failed') return;
    clearPendingCheckout();
    if (state.phase !== 'success') return;
    if (state.anonymous) return;
    const t = setTimeout(() => navigate(dashboardPath), SUCCESS_REDIRECT_MS);
    return () => clearTimeout(t);
  }, [state.phase, state.anonymous, dashboardPath, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="subscribe-return">
      <Card className="max-w-md w-full border-slate-200 shadow-sm">
        <CardContent className="p-8 text-center">
          {state.phase === 'polling' && (
            <>
              <div className="w-16 h-16 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-4">
                <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Activating Your Subscription
              </h2>
              <p className="text-sm text-slate-600">
                Stripe is confirming the payment — this takes a few seconds.
              </p>
              <p className="text-xs text-slate-400 mt-3">Attempt {attempts + 1} of {MAX_POLL_ATTEMPTS}</p>
            </>
          )}

          {state.phase === 'success' && (
            <>
              <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-8 h-8 text-emerald-600" />
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Subscription Active
              </h2>
              <p className="text-sm text-slate-600 mb-2">
                Your {state.data?.metadata?.plan_id} plan is now live. Welcome aboard.
              </p>
              {state.data?.amount && (
                <p className="text-xs text-slate-500 mb-2">
                  First charge: ${state.data.amount.toFixed(2)} {(state.data.currency || 'usd').toUpperCase()}
                </p>
              )}
              {state.anonymous ? (
                <>
                  <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-2 mb-4">
                    You&apos;re not signed in on this browser. Sign back in to
                    access your newly-activated subscription.
                  </p>
                  <Button onClick={() => navigate('/login')}
                          className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                          data-testid="goto-login-btn">
                    Sign In
                  </Button>
                </>
              ) : (
                <>
                  <p className="text-xs text-slate-400 mb-6">Redirecting to Dashboard…</p>
                  <Button onClick={() => navigate(dashboardPath)}
                          className="w-full bg-teal-700 hover:bg-teal-800 text-white"
                          data-testid="goto-dashboard-btn">
                    Go to Dashboard
                  </Button>
                </>
              )}
            </>
          )}

          {state.phase === 'failed' && (
            <>
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                {state.data?.status === 'EXPIRED'
                  ? <Clock className="w-8 h-8 text-rose-600" />
                  : <XCircle className="w-8 h-8 text-rose-600" />}
              </div>
              <h2 className="text-xl font-semibold text-slate-900 mb-2"
                  style={{ fontFamily: 'Manrope, sans-serif' }}>
                Subscription Not Activated
              </h2>
              <p className="text-sm text-slate-600 mb-6">
                {state.error || 'The payment did not go through. No charges were made.'}
              </p>
              <Button onClick={() => navigate(subscribePath)}
                      variant="outline" className="w-full border-slate-200">
                Try Again
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
